import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MainState } from "@app/store/state";
import { AuthV2ApiService } from "@core/services/api/v2/auth/auth-v2-api.service";
import { AuthService } from "@core/services/auth.service";
import { DeviceService } from "@core/services/device.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { environment } from "@env/environment";
import { Verify2FA, Auth2FAActionTypes } from "@features/account/store/auth-2fa.actions";
import { AuthActionTypes } from "@features/account/store/auth.actions";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { OtpInputComponent } from "@shared/components/auth/otp-input/otp-input.component";
import { interval, Subject } from "rxjs";
import { takeUntil, take } from "rxjs/operators";

@Component({
  selector: "astrobin-two-factor-form",
  templateUrl: "./two-factor-form.component.html",
  styleUrls: ["./two-factor-form.component.scss"]
})
export class TwoFactorFormComponent implements OnInit, OnDestroy {
  @Input() twoFactorMethod: "totp" | "email";
  @Input() emailHint: string;
  @Input() sessionTimeout = environment.authSessionTimeout || 1200;

  @Output() verificationSuccess = new EventEmitter<any>();
  @Output() verificationFailure = new EventEmitter<any>();
  @Output() sessionExpired = new EventEmitter<void>();
  @Output() backToLogin = new EventEmitter<void>();

  @ViewChild(OtpInputComponent) otpInputComponent: OtpInputComponent;

  form: FormGroup;
  timeRemaining: number;
  displayTime: string;
  isVerifying = false;
  hasError = false;
  errorMessage: string = null;

  // Resend throttling
  canResendAt: number = 0;
  resendCooldownSeconds = 60; // 60 seconds between resends
  resendAttemptsCount = 0;
  maxResendAttempts = 3;
  resendCountdown: number = 0;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly authV2Api: AuthV2ApiService,
    private readonly deviceService: DeviceService,
    private readonly loadingService: LoadingService,
    private readonly popNotificationsService: PopNotificationsService,
    private readonly translateService: TranslateService,
    private readonly store$: Store<MainState>,
    private readonly actions$: Actions,
    private readonly utilsService: UtilsService
  ) {
    this.form = this.formBuilder.group({
      otpCode: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit(): void {
    this.timeRemaining = this.sessionTimeout;
    this.updateDisplayTime();
    this.startCountdown();

    if (!this.twoFactorMethod) {
      this.twoFactorMethod = this.authService.getTwoFactorMethod();
    }

    if (!this.emailHint && this.twoFactorMethod === "email") {
      this.emailHint = this.authService.getEmailHint();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onOtpComplete(code: string): void {
    this.form.patchValue({ otpCode: code });
    if (this.form.valid) {
      this.verify();
    }
  }

  onOtpChange(code: string): void {
    this.form.patchValue({ otpCode: code });
    this.hasError = false;
    this.errorMessage = null;
  }

  verify(): void {
    if (!this.form.valid || this.isVerifying) {
      return;
    }

    const otpCode = this.form.get("otpCode").value;
    this.isVerifying = true;
    this.hasError = false;

    // Disable the form control while verifying
    this.form.get("otpCode").disable();

    // Dispatch the verify action
    this.store$.dispatch(new Verify2FA({ otpCode }));

    // Listen for success
    this.actions$.pipe(ofType(AuthActionTypes.LOGIN_SUCCESS), take(1), takeUntil(this.destroyed$)).subscribe(() => {
      this.isVerifying = false;
      this.form.get("otpCode").enable();
      // Success is handled by the effect which navigates to logged-in page
    });

    // Listen for failure
    this.actions$
      .pipe(
        ofType(Auth2FAActionTypes.VERIFY_2FA_FAILURE, Auth2FAActionTypes.SESSION_EXPIRED),
        take(1),
        takeUntil(this.destroyed$)
      )
      .subscribe((action: any) => {
        this.isVerifying = false;
        this.form.get("otpCode").enable();
        this.hasError = true;

        if (action.type === Auth2FAActionTypes.SESSION_EXPIRED) {
          this.handleSessionExpired();
        } else {
          // Set error message for inline display
          if (action.payload?.error?.error === "Invalid OTP code") {
            this.errorMessage = this.translateService.instant("Invalid verification code. Please try again.");
          } else if (action.payload?.error?.error === "Invalid or expired session token") {
            this.errorMessage = this.translateService.instant("Your session has expired. Please log in again.");
          } else {
            this.errorMessage = this.translateService.instant("Verification failed. Please try again.");
          }
          this.form.get("otpCode").setValue("");
          // Refocus the first input after clearing
          this.focusFirstInput();
        }
      });
  }

  resendCode(): void {
    if (this.twoFactorMethod !== "email") {
      return;
    }

    const now = Date.now();

    // Check if we're still in cooldown
    if (now < this.canResendAt) {
      const secondsRemaining = Math.ceil((this.canResendAt - now) / 1000);
      this.popNotificationsService.warning(
        this.translateService.instant("Please wait {{seconds}} seconds before requesting a new code.", {
          seconds: secondsRemaining
        })
      );
      return;
    }

    // Check if max attempts reached
    if (this.resendAttemptsCount >= this.maxResendAttempts) {
      this.popNotificationsService.error(
        this.translateService.instant("Maximum resend attempts reached. Please try logging in again.")
      );
      return;
    }

    // Update counters immediately to prevent double-clicks
    this.resendAttemptsCount++;
    this.canResendAt = now + this.resendCooldownSeconds * 1000;
    this.resendCountdown = this.resendCooldownSeconds;

    const sessionToken = this.authService.getSessionToken();
    if (!sessionToken) {
      this.popNotificationsService.error(this.translateService.instant("Session expired. Please log in again."));
      this.goBackToLogin();
      return;
    }

    // Call the resend API
    this.authV2Api.resend2FACode(sessionToken).subscribe(
      response => {
        if (response.success) {
          this.popNotificationsService.success(
            this.translateService.instant("A new code has been sent to your email.")
          );
        } else {
          this.popNotificationsService.error(
            response.message || this.translateService.instant("Failed to resend code. Please try again.")
          );
          // Rollback the counters on failure
          this.resendAttemptsCount--;
          this.canResendAt = 0;
          this.resendCountdown = 0;
        }
      },
      error => {
        this.popNotificationsService.error(this.translateService.instant("Failed to resend code. Please try again."));
        // Rollback the counters on error
        this.resendAttemptsCount--;
        this.canResendAt = 0;
        this.resendCountdown = 0;
      }
    );
  }

  canResend(): boolean {
    return (
      this.twoFactorMethod === "email" &&
      this.resendCountdown === 0 &&
      this.resendAttemptsCount < this.maxResendAttempts
    );
  }

  getResendButtonText(): string {
    if (this.resendCountdown > 0) {
      return this.translateService.instant("Resend code ({{seconds}}s)", { seconds: this.resendCountdown });
    } else if (this.resendAttemptsCount >= this.maxResendAttempts) {
      return this.translateService.instant("Maximum attempts reached");
    } else {
      return this.translateService.instant("Didn't receive the code? Resend");
    }
  }

  goBackToLogin(): void {
    this.authService.clear2FASession();
    this.backToLogin.emit();
  }

  private startCountdown(): void {
    interval(1000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.timeRemaining--;
        this.updateDisplayTime();

        // Update resend countdown
        if (this.resendCountdown > 0) {
          this.resendCountdown--;
        }

        if (this.timeRemaining <= 0) {
          this.handleSessionExpired();
        }
      });
  }

  private updateDisplayTime(): void {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;
    this.displayTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  private handleSessionExpired(): void {
    this.authService.clear2FASession();
    this.popNotificationsService.warning(
      this.translateService.instant("Your session has expired. Please log in again.")
    );
    this.sessionExpired.emit();
  }

  getMethodDescription(): string {
    if (this.twoFactorMethod === "totp") {
      return this.translateService.instant("Enter the 6-digit code from your authenticator app");
    } else if (this.twoFactorMethod === "email") {
      return this.translateService.instant("Enter the 6-digit code sent to {{email}}", {
        email: this.emailHint || "your email"
      });
    }
    return "";
  }

  private focusFirstInput(): void {
    // Focus the first OTP input after a brief delay (skip on touch devices)
    if (this.otpInputComponent && !this.deviceService.isTouchEnabled()) {
      // Use a small delay to ensure the form has been updated
      this.utilsService.delay(100).subscribe(() => {
        this.otpInputComponent.focusFirst();
      });
    }
  }
}
